import { useState } from "react";
import { CheckCircle2, Circle, Dot, X } from "lucide-react";

import BookSummary, {
  BookMenuActions,
  ContentDetailsParameters,
} from "@components/layouts/b2c/content/BookSummary";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@ui/resizable";
import { Card } from "@ui/card";
import { getPublicMediaUrl } from "@modules/api/util/mediaHelper";
import { convertMsToHourMinSec } from "@utils/helpers/timeFormtter";
import { ShadcnAccordion } from "@components/items/display/AccordionShadcn";
import { Menu2Icon } from "@components/icons/Menu2Icon";
import { ScrollArea } from "@ui/scroll-area";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { Server } from "@wajeez/wajeez.microservices";

import TranslatedText from "@components/items/display/TranslatedText";
import { useLockedContents } from "@modules/content/hooks/useLockedContent";
import { Reader } from "../details/Reader";
import FullBookSummary, {
  FullBookContentDetailsParameters,
} from "@components/layouts/b2c/content/FullBookSummary";
import ContentComponentSelector from "@modules/content/components";

export type ReaderStyle = {
  fontSize: number;
  fontType: { value: string; label: string };
  backgroundColor: string | undefined;
};

interface FullBookContentDetailsProps {
  content: Server.ContentEntityOutput | undefined;
  params: Partial<FullBookContentDetailsParameters>;
  progress: Server.ContentEntityProgressOutput | undefined;
  chapters: Server.ItemContentOutput[];
  playingChapterId: string | undefined;
  menu: BookMenuActions;
  onListen: () => void;
  onListenToChapter: () => void;
  onRead?: (id?: string) => void;
  readerShown?: boolean;
  onReaderClose?: () => void;
  isLocked?: boolean;
}

export const FullBookContentDetails = ({
  content,
  params,
  progress,
  chapters,
  playingChapterId,
  menu,
  onListen,
  onListenToChapter,
  onRead,
  readerShown,
  onReaderClose,
  isLocked,
}: FullBookContentDetailsProps) => {
  const navigate = useAppNavigate();

  const renderChapterOrSummary = () => {
    return (
      <div className="relative">
        <div
          className="absolute top-4 cursor-pointer text-black ltr:right-4 rtl:left-4 dark:text-white"
          onClick={() => {
            if (window.history.state.idx !== 0) {
              navigate(-1);
            } else {
              navigate("/");
            }
          }}
        >
          <X className="stroke-4 h-5 w-5" />
        </div>
        <FullBookSummary
          id={content?.id}
          title={content?.title}
          author={content?.authors?.join(", ")}
          durationMS={content?.durationMS}
          rate={content?.rate}
          progress={Number(
            ((progress?.currentProgress || 0) * 100)?.toFixed(0)
          )}
          menu={menu}
          onListenClick={onListen}
          isLocked={isLocked}
          {...params}
        />
      </div>
    );
  };

  return (
    <div className={`h-full w-full bg-white dark:bg-black`}>
      <div className="cus-scrollbar-w-none h-full overflow-y-auto pb-16">
        {renderChapterOrSummary()}
      </div>
    </div>
  );
};
